import type { SnowplowContext } from './snowplow-contexts';
import type { TrackingProviderPropertiesInterface } from './tracking-provider';

import { useRoutingStore } from '@/stores/pinia/useRoutingStore';
// BR_SUBDOMAIN
import { BR_HOST } from '@/routing/helpers/subdomain-helper';
// BR_SUBDOMAIN

import { TrackingProvider } from './tracking-provider';
import { getUtmSearchParams } from '@/helpers/composables/useArbitrage';

let lastPath = '';

let resolveInit: () => void;
const snowplowInitPromise = new Promise<void>(res => (resolveInit = res));

async function tracker(...args: any) {
	if (typeof window === 'undefined') return;

	await snowplowInitPromise;

	try {
		window.snowplow(...args);
	} catch (err) {
		console.error('[snowplow] function not found in window object', err);
	}
}

const attachSnowplow = (p: any, l: any, o: any, w: any, i: any, n?: any, g?: any) => {
	if (!p[i]) {
		p.GlobalSnowplowNamespace = p.GlobalSnowplowNamespace || [];
		p.GlobalSnowplowNamespace.push(i);
		p[i] = function () {
			(p[i].q = p[i].q || []).push(arguments);
		};
		p[i].q = p[i].q || [];
		n = l.createElement(o);
		g = l.getElementsByTagName(o)[0];
		n.async = 1;
		n.defer = 1;
		n.src = w;
		g.parentNode.insertBefore(n, g);
	}
};

interface SnowplowConfig {
	userId: string;
	appId: string;
	cookieDomain: string;
	platform: 'web' | 'mob';
	doNotTrack: boolean;
	eventMethod: string;
	postPath: string;
}

export class TrackingProviderSnowplow extends TrackingProvider {
	dataProcessingService = null;

	constructor(private config: SnowplowConfig) {
		super();

		// config validation
		if (!this.config.appId) {
			throw new Error('Expected "config.appId"');
		}
		if (!this.config.cookieDomain) {
			throw new Error('Expected "config.cookieDomain"');
		}
	}

	/**
	 * Helper to get the domain dynamically based on activeDomain from the store.
	 * Uses the real visible hostname when it's BR_HOST, since Brazil is reachable at its own
	 * public hostname while `activeDomain` itself stays `www` (same content/routing scoping).
	 */
	#getDomain(): string {
		const routingStore = useRoutingStore();
		// BR_SUBDOMAIN
		if (routingStore.isBrPublicHost) return `https://${BR_HOST}`;
		// BR_SUBDOMAIN
		return `https://${routingStore.activeDomain}.${JW_CONFIG.DOMAIN}`;
	}

	initialize() {
		// failsafe for SSR
		if (typeof window === 'undefined' || import.meta.server) {
			return Promise.resolve();
		}

		return new Promise<void>(async resolve => {
			// ignore config.doNotTrack; it's first party data
			const splib = `/${ASSETS_DIR}/js/core-380.min.js`;
			if (!('snowplow' in window)) {
				attachSnowplow(window, document, 'script', splib, 'snowplow');
			}

			tracker('newTracker', 'cf', `https://${JW_CONFIG.SNOWPLOW_COLLECTOR_ENDPOINT}`, this.config);

			tracker('setUserId', this.config.userId);

			resolveInit?.();
			resolve();
		});
	}

	async trackPage({ path, title, contexts = [] }: { path?: string; title?: string; contexts: SnowplowContext[] }) {
		const utmSearchParams = getUtmSearchParams();
		const DOMAIN = this.#getDomain();

		if (lastPath) {
			const url = applySearchParams(new URL(`${DOMAIN}${lastPath}`), utmSearchParams);
			tracker('setReferrerUrl', url.href);
		}

		const url = applySearchParams(new URL(`${DOMAIN}${path}`), utmSearchParams);
		tracker('setCustomUrl', url.href);
		tracker('trackPageView', {
			title,
			context: contexts.map(c => c.toObject()),
		});

		lastPath = path ? path : '';
	}

	refreshPermanentAudiences(_permanent_audiences: string[] = []) {}
	refreshPermanentAudiencesSubgenres(_permanent_audiences: string[]): void {}

	async trackEvent(category: string, properties: TrackingProviderPropertiesInterface, contexts: SnowplowContext[]) {
		const DOMAIN = this.#getDomain();

		if (lastPath) {
			tracker('setReferrerUrl', `${DOMAIN}${lastPath}`);
		}

		const { action, label, property, value } = properties;

		tracker('trackStructEvent', {
			category,
			action,
			label,
			property,
			value,
			context: contexts.map(c => c.toObject()),
		});
	}
}

function applySearchParams(url: URL, params: URLSearchParams | null) {
	if (params) {
		params.forEach((value, key) => url.searchParams.set(key, value));
	}

	return url;
}
